/**
 * KPIs por ejercicio (§5.1): tonelaje, e1RM, récords, progresión, estancamiento.
 *
 * Regla transversal (§4.3): **las series omitidas se excluyen, no valen cero.** Promediar
 * un cero porque las mancuernas estaban ocupadas inventa un bajón de rendimiento que no
 * ocurrió. Toda función de este archivo filtra por `completada` antes de agregar.
 */

import { claveSemana, enVentana, ventana } from '../dates.ts';
import type { Fecha, MotivoOmision, Serie, SerieContexto, Unidad } from '../types.ts';
import { enUnidad } from '../units.ts';

export type FormulaE1RM = 'epley' | 'brzycki';

/**
 * §5.1 — texto obligatorio en toda UI que muestre un e1RM. Vive acá para que no haya dos
 * redacciones distintas dando vueltas.
 */
export const E1RM_ADVERTENCIA =
  'El e1RM está validado para ejercicios libres. En máquinas de placas la polea y la ' +
  'palanca alteran la carga real: este número sirve sólo para compararte contigo mismo a ' +
  'lo largo del tiempo, nunca entre máquinas distintas ni contra otras personas.';

export const seriesEfectivas = (ctxs: SerieContexto[]): SerieContexto[] =>
  ctxs.filter((c) => c.serie.completada);

// ---------------------------------------------------------------------------
// Tonelaje
// ---------------------------------------------------------------------------

/** reps × peso de una serie, en la unidad pedida. */
export function volumenSerie(s: Serie, unidad: Unidad = 'kg'): number {
  if (!s.completada) return 0;
  return s.reps * enUnidad({ valor: s.peso, unidad: s.unidad }, unidad);
}

/**
 * Tonelaje = Σ(reps × peso) sobre las series completadas. Se agrega en una unidad común
 * porque un mismo día puede mezclar una máquina en libras con mancuernas en kilos; sumar
 * los números crudos daría un total sin significado.
 */
export function volumen(ctxs: SerieContexto[], unidad: Unidad = 'kg'): number {
  let total = 0;
  for (const c of seriesEfectivas(ctxs)) total += volumenSerie(c.serie, unidad);
  return total;
}

export function volumenPorSesion(
  ctxs: SerieContexto[],
  unidad: Unidad = 'kg',
): Map<string, number> {
  const out = new Map<string, number>();
  for (const c of seriesEfectivas(ctxs)) {
    out.set(c.sesion_id, (out.get(c.sesion_id) ?? 0) + volumenSerie(c.serie, unidad));
  }
  return out;
}

/** Intensidad media = tonelaje / reps totales. El peso promedio con que se trabajó. */
export function intensidadMedia(ctxs: SerieContexto[], unidad: Unidad = 'kg'): number | null {
  const efectivas = seriesEfectivas(ctxs);
  const reps = efectivas.reduce((a, c) => a + c.serie.reps, 0);
  if (reps === 0) return null;
  return volumen(efectivas, unidad) / reps;
}

// ---------------------------------------------------------------------------
// 1RM estimado
// ---------------------------------------------------------------------------

/**
 * §5.1. Con 1 rep el estimado ES el peso levantado: aplicar la fórmula ahí devolvería un
 * máximo mayor que un máximo real, que es justo lo contrario de lo que se busca.
 * Brzycki se indefine cerca de 37 reps (denominador → 0); por encima de 36 no se estima.
 */
export function e1rm(peso: number, reps: number, formula: FormulaE1RM = 'epley'): number | null {
  if (!Number.isFinite(peso) || !Number.isFinite(reps) || peso <= 0 || reps < 1) return null;
  if (reps === 1) return peso;

  if (formula === 'brzycki') {
    if (reps > 36) return null;
    return peso * (36 / (37 - reps));
  }
  return peso * (1 + reps / 30);
}

export function e1rmSerie(
  s: Serie,
  unidad: Unidad = 'kg',
  formula: FormulaE1RM = 'epley',
): number | null {
  if (!s.completada) return null;
  return e1rm(enUnidad({ valor: s.peso, unidad: s.unidad }, unidad), s.reps, formula);
}

/** El mejor e1RM del conjunto, con la serie que lo produjo. */
export function mejorE1rm(
  ctxs: SerieContexto[],
  unidad: Unidad = 'kg',
  formula: FormulaE1RM = 'epley',
): { valor: number; unidad: Unidad; fecha: Fecha; serie: Serie } | null {
  let mejor: { valor: number; unidad: Unidad; fecha: Fecha; serie: Serie } | null = null;
  for (const c of seriesEfectivas(ctxs)) {
    const v = e1rmSerie(c.serie, unidad, formula);
    if (v === null) continue;
    if (!mejor || v > mejor.valor) mejor = { valor: v, unidad, fecha: c.fecha, serie: c.serie };
  }
  return mejor;
}

// ---------------------------------------------------------------------------
// Última marca — para precargar el registro (§4.1)
// ---------------------------------------------------------------------------

export interface UltimaMarca {
  fecha: Fecha;
  sesion_id: string;
  series: Serie[];
  /** La serie de referencia para precargar: la de mayor peso, y a igual peso, más reps. */
  referencia: Serie;
  maquina_id: string | null;
}

/**
 * Lo último que hizo el usuario en ese ejercicio. §4.1: al abrir un ejercicio se precarga
 * esto, porque el caso normal es repetir o subir un escalón, no escribir desde cero.
 */
export function ultimaMarca(ctxs: SerieContexto[], ejercicioId: string): UltimaMarca | null {
  const delEjercicio = ctxs
    .filter((c) => c.serie.ejercicio_id === ejercicioId && c.serie.completada)
    .sort((a, b) => (a.fecha < b.fecha ? 1 : a.fecha > b.fecha ? -1 : 0));

  const ultima = delEjercicio[0];
  if (!ultima) return null;

  const deEsaSesion = delEjercicio
    .filter((c) => c.sesion_id === ultima.sesion_id)
    .map((c) => c.serie)
    .sort((a, b) => a.numero_serie - b.numero_serie);

  const referencia = [...deEsaSesion].sort((a, b) => {
    const pa = enUnidad({ valor: a.peso, unidad: a.unidad }, 'kg');
    const pb = enUnidad({ valor: b.peso, unidad: b.unidad }, 'kg');
    return pb - pa || b.reps - a.reps;
  })[0]!;

  return {
    fecha: ultima.fecha,
    sesion_id: ultima.sesion_id,
    series: deEsaSesion,
    referencia,
    maquina_id: referencia.maquina_id,
  };
}

// ---------------------------------------------------------------------------
// Récords personales (§5.1)
// ---------------------------------------------------------------------------

export interface MarcaPeso {
  valor: number;
  unidad: Unidad;
  reps: number;
  fecha: Fecha;
  sesion_id: string;
}

export interface RecordsEjercicio {
  ejercicio_id: string;
  /** Peso máximo levantado, en la unidad original en que se registró. */
  pesoMax: MarcaPeso | null;
  /** Más reps en una serie, sin importar el peso. */
  repsMax: MarcaPeso | null;
  /** Volumen máximo de una sesión, en kg. */
  volumenSesionMax: { volumen: number; unidad: Unidad; fecha: Fecha; sesion_id: string } | null;
  e1rmMax: { valor: number; unidad: Unidad; fecha: Fecha } | null;
}

export function recordsDeEjercicio(
  ctxs: SerieContexto[],
  ejercicioId: string,
  formula: FormulaE1RM = 'epley',
): RecordsEjercicio {
  const delEj = seriesEfectivas(ctxs).filter((c) => c.serie.ejercicio_id === ejercicioId);

  let pesoMax: MarcaPeso | null = null;
  let repsMax: MarcaPeso | null = null;
  for (const c of delEj) {
    const s = c.serie;
    const enKgVal = enUnidad({ valor: s.peso, unidad: s.unidad }, 'kg');
    const marca: MarcaPeso = {
      valor: s.peso,
      unidad: s.unidad,
      reps: s.reps,
      fecha: c.fecha,
      sesion_id: c.sesion_id,
    };
    if (!pesoMax || enKgVal > enUnidad({ valor: pesoMax.valor, unidad: pesoMax.unidad }, 'kg')) {
      pesoMax = marca;
    }
    if (!repsMax || s.reps > repsMax.reps) repsMax = marca;
  }

  let volumenSesionMax: RecordsEjercicio['volumenSesionMax'] = null;
  for (const [sesionId, vol] of volumenPorSesion(delEj, 'kg')) {
    if (!volumenSesionMax || vol > volumenSesionMax.volumen) {
      const fecha = delEj.find((c) => c.sesion_id === sesionId)!.fecha;
      volumenSesionMax = { volumen: vol, unidad: 'kg', fecha, sesion_id: sesionId };
    }
  }

  const mejor = mejorE1rm(delEj, 'kg', formula);
  return {
    ejercicio_id: ejercicioId,
    pesoMax,
    repsMax,
    volumenSesionMax,
    e1rmMax: mejor ? { valor: mejor.valor, unidad: 'kg', fecha: mejor.fecha } : null,
  };
}

export type TipoRecord = 'peso' | 'reps' | 'volumen_sesion' | 'e1rm';

/**
 * Qué récords rompe una serie nueva frente al historial ANTERIOR a ella. El historial que
 * se pasa no debe incluir la serie que se está evaluando, o nunca detectaría nada.
 */
export function recordsRotos(
  nueva: SerieContexto,
  historialPrevio: SerieContexto[],
  formula: FormulaE1RM = 'epley',
): TipoRecord[] {
  if (!nueva.serie.completada) return [];
  const previos = recordsDeEjercicio(historialPrevio, nueva.serie.ejercicio_id, formula);
  const rotos: TipoRecord[] = [];

  const pesoKg = enUnidad({ valor: nueva.serie.peso, unidad: nueva.serie.unidad }, 'kg');
  const previoKg = previos.pesoMax
    ? enUnidad({ valor: previos.pesoMax.valor, unidad: previos.pesoMax.unidad }, 'kg')
    : null;
  if (previoKg === null || pesoKg > previoKg) rotos.push('peso');
  if (!previos.repsMax || nueva.serie.reps > previos.repsMax.reps) rotos.push('reps');

  const nuevoE1rm = e1rmSerie(nueva.serie, 'kg', formula);
  if (nuevoE1rm !== null && (!previos.e1rmMax || nuevoE1rm > previos.e1rmMax.valor)) {
    rotos.push('e1rm');
  }
  return rotos;
}

// ---------------------------------------------------------------------------
// Progresión semana a semana (§5.1)
// ---------------------------------------------------------------------------

export interface SemanaEjercicio {
  semana: string;
  pesoMaxKg: number;
  repsTotales: number;
  volumenKg: number;
  series: number;
  e1rmMaxKg: number | null;
}

export function progresionSemanal(
  ctxs: SerieContexto[],
  ejercicioId: string,
  formula: FormulaE1RM = 'epley',
): SemanaEjercicio[] {
  const porSemana = new Map<string, SerieContexto[]>();
  for (const c of seriesEfectivas(ctxs)) {
    if (c.serie.ejercicio_id !== ejercicioId) continue;
    const k = claveSemana(c.fecha);
    const arr = porSemana.get(k);
    if (arr) arr.push(c);
    else porSemana.set(k, [c]);
  }

  return [...porSemana.entries()]
    .map(([semana, grupo]) => {
      const mejor = mejorE1rm(grupo, 'kg', formula);
      return {
        semana,
        pesoMaxKg: Math.max(
          ...grupo.map((c) => enUnidad({ valor: c.serie.peso, unidad: c.serie.unidad }, 'kg')),
        ),
        repsTotales: grupo.reduce((a, c) => a + c.serie.reps, 0),
        volumenKg: volumen(grupo, 'kg'),
        series: grupo.length,
        e1rmMaxKg: mejor?.valor ?? null,
      };
    })
    .sort((a, b) => (a.semana < b.semana ? -1 : 1));
}

// ---------------------------------------------------------------------------
// Estancamiento (§5.1, §7)
// ---------------------------------------------------------------------------

/** Firma de lo hecho en un ejercicio dentro de una sesión: pares peso-reps, ordenados. */
function firmaSesion(series: Serie[]): string {
  return series
    .map((s) => `${enUnidad({ valor: s.peso, unidad: s.unidad }, 'kg').toFixed(3)}x${s.reps}`)
    .sort()
    .join('|');
}

export interface Estancamiento {
  ejercicio_id: string;
  sesiones: number;
  desde: Fecha;
  hasta: Fecha;
  firma: string;
}

/**
 * §7 — mismo peso y mismas reps durante `minimo` sesiones consecutivas del mismo ejercicio.
 * Se comparan sesiones consecutivas *de ese ejercicio*, no días de calendario: si el
 * usuario entrena pierna cada 5 días, tres sesiones estancadas son 15 días, y sigue siendo
 * un estancamiento.
 */
export function detectarEstancamiento(
  ctxs: SerieContexto[],
  ejercicioId: string,
  minimo = 3,
): Estancamiento | null {
  const porSesion = new Map<string, { fecha: Fecha; series: Serie[] }>();
  for (const c of seriesEfectivas(ctxs)) {
    if (c.serie.ejercicio_id !== ejercicioId) continue;
    const entrada = porSesion.get(c.sesion_id);
    if (entrada) entrada.series.push(c.serie);
    else porSesion.set(c.sesion_id, { fecha: c.fecha, series: [c.serie] });
  }

  const sesiones = [...porSesion.values()]
    .map((s) => ({ fecha: s.fecha, firma: firmaSesion(s.series) }))
    .sort((a, b) => (a.fecha < b.fecha ? -1 : a.fecha > b.fecha ? 1 : 0));

  if (sesiones.length < minimo) return null;

  // Se mira la racha que termina en la última sesión: un estancamiento viejo ya resuelto
  // no debería seguir alertando.
  const ultima = sesiones[sesiones.length - 1]!;
  let racha = 1;
  for (let i = sesiones.length - 2; i >= 0; i--) {
    if (sesiones[i]!.firma !== ultima.firma) break;
    racha++;
  }
  if (racha < minimo) return null;

  return {
    ejercicio_id: ejercicioId,
    sesiones: racha,
    desde: sesiones[sesiones.length - racha]!.fecha,
    hasta: ultima.fecha,
    firma: ultima.firma,
  };
}

// ---------------------------------------------------------------------------
// Omisiones (§4.3, §7)
// ---------------------------------------------------------------------------

/**
 * Omisiones consecutivas por un motivo dado, mirando desde la última sesión hacia atrás.
 *
 * Las sesiones donde el ejercicio quedó sólo *pendiente* — planificado o arrastrado, pero
 * todavía sin hacer ni omitir — se saltean. Si contaran, una sesión futura con el ejercicio
 * en la lista taparía una racha real de omisiones, y la alerta de dolor recurrente de §7 no
 * se dispararía nunca.
 *
 * Una omisión por otro motivo sí corta la racha: "omitido por dolor dos veces seguidas" es
 * un patrón, y dolor → sin tiempo → dolor no lo es.
 */
export function omisionesConsecutivas(
  ctxs: SerieContexto[],
  ejercicioId: string,
  motivo: MotivoOmision,
): number {
  type Estado = 'pendiente' | 'hecho' | 'omitido_motivo' | 'omitido_otro';

  const porSesion = new Map<string, { fecha: Fecha; estado: Estado }>();
  for (const c of ctxs) {
    if (c.serie.ejercicio_id !== ejercicioId) continue;

    const estadoSerie: Estado = c.serie.completada
      ? 'hecho'
      : c.serie.motivo_omision === motivo
        ? 'omitido_motivo'
        : c.serie.motivo_omision === null
          ? 'pendiente'
          : 'omitido_otro';

    const previo = porSesion.get(c.sesion_id);
    if (!previo) {
      porSesion.set(c.sesion_id, { fecha: c.fecha, estado: estadoSerie });
      continue;
    }
    // Dentro de una sesión: haber hecho aunque sea una serie manda sobre la omisión, y
    // cualquier resolución manda sobre "pendiente".
    if (estadoSerie === 'hecho') previo.estado = 'hecho';
    else if (previo.estado === 'pendiente') previo.estado = estadoSerie;
  }

  const ordenadas = [...porSesion.values()]
    .filter((s) => s.estado !== 'pendiente')
    .sort((a, b) => (a.fecha < b.fecha ? 1 : a.fecha > b.fecha ? -1 : 0));

  let n = 0;
  for (const s of ordenadas) {
    if (s.estado !== 'omitido_motivo') break;
    n++;
  }
  return n;
}

/** Tonelaje en una ventana móvil que termina en `hasta`. */
export function volumenEnVentana(
  ctxs: SerieContexto[],
  hasta: Fecha,
  dias: number,
  unidad: Unidad = 'kg',
): number {
  const v = ventana(hasta, dias);
  return volumen(
    ctxs.filter((c) => enVentana(c.fecha, v)),
    unidad,
  );
}
